import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, GraduationCap, History, Laptop, Pencil, ShieldAlert, Timer } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { DueBadge, LoginEventBadge, PersonStatusBadge, PersonTypeBadge } from '../components/badges';
import { CheckInDialog, CheckOutDialog, type CheckoutAsset } from '../components/checkout-dialogs';
import { LendLoanerModal } from '../components/loaner-dialog';
import { AssignedPartsCard } from '../components/part-dialogs';
import { PersonAlertsBanner, PersonFieldsCard } from '../components/person-fields';
import { PersonFormModal } from '../components/PersonFormModal';
import { AssetSearch } from '../components/pickers';
import { RemoveRecordsDialog, useIsEdvoraStaff } from '../components/remove-records';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DescriptionList,
  EmptyState,
  ErrorState,
  Modal,
  PageHeader,
  PageLoader,
  Table,
  Td,
  Th,
} from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime, formatRelative, personName } from '../lib/format';
import { gradeLabel, schoolYearLabel } from '../lib/oneroster';
import type { Checkout, PersonDetail } from '../lib/types';

/** Edvora staff only: takes the person and their history out of this school. */
function RemovePerson({ personId, name }: { personId: string; name: string }) {
  const navigate = useNavigate();
  const [removing, setRemoving] = useState(false);
  const edvora = useIsEdvoraStaff();
  if (!edvora) return null;
  return (
    <>
      <Button variant="ghost" icon={<ShieldAlert />} onClick={() => setRemoving(true)} title={`Remove ${name} and their history`}>
        Remove (Edvora)
      </Button>
      <RemoveRecordsDialog open={removing} onClose={() => setRemoving(false)} subjects={{ personIds: [personId] }} onRemoved={() => navigate('/people')} />
    </>
  );
}

export function PersonDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const [pickingAsset, setPickingAsset] = useState(false);
  const [checkoutAsset, setCheckoutAsset] = useState<CheckoutAsset | null>(null);
  const [checkingIn, setCheckingIn] = useState<Checkout | null>(null);
  const [lending, setLending] = useState(false);

  const query = useQuery({ queryKey: ['person', id], queryFn: () => api.get<PersonDetail>(`/people/${id}`) });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const person = query.data;
  const name = personName(person);
  const active = person.checkouts.filter((c) => !c.checkedInAt);
  const past = person.checkouts.filter((c) => c.checkedInAt);
  const canWrite = can('TECHNICIAN');

  return (
    <>
      <PageHeader
        back={{ to: '/people', label: 'People' }}
        title={
          <>
            {name}
            {person.preferredName && person.preferredName !== person.firstName && (
              <span className="ml-2 text-base font-normal text-slate-400">({person.firstName})</span>
            )}
          </>
        }
        meta={
          <>
            <PersonTypeBadge type={person.type} />
            <PersonStatusBadge status={person.status} />
            {person.source === 'BLACKBAUD' ? (
              <Badge tone="brand">Blackbaud · synced {formatRelative(person.lastSyncedAt)}</Badge>
            ) : person.source === 'ONEROSTER' ? (
              <Badge tone="brand">SIS · synced {formatRelative(person.lastSyncedAt)}</Badge>
            ) : (
              <Badge>{person.source === 'IMPORT' ? 'Imported' : 'Added manually'}</Badge>
            )}
          </>
        }
        actions={
          canWrite && (
            <>
              <RemovePerson personId={person.id} name={name} />
              <Button icon={<Pencil />} onClick={() => setEditing(true)}>
                {person.source === 'MANUAL' ? 'Edit' : 'Notes'}
              </Button>
              {person.status === 'ACTIVE' && (
                <>
                  <Button icon={<Timer />} onClick={() => setLending(true)}>
                    Lend a loaner
                  </Button>
                  <Button variant="primary" icon={<ArrowLeftRight />} onClick={() => setPickingAsset(true)}>
                    Check out an asset
                  </Button>
                </>
              )}
            </>
          )
        }
      />

      <PersonAlertsBanner personId={person.id} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Currently assigned" description={`${active.length} asset${active.length === 1 ? '' : 's'}`} />
            {active.length === 0 ? (
              <EmptyState title="Nothing checked out" />
            ) : (
              <ul className="divide-y divide-slate-100">
                {active.map((checkout) => (
                  <li key={checkout.id} className="flex flex-wrap items-center gap-4 px-5 py-3 text-sm">
                    <Link to={`/assets/${checkout.asset.id}`} className="w-24 font-mono text-xs font-semibold text-brand-800 hover:underline">
                      {checkout.asset.assetTag}
                    </Link>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-900">{checkout.asset.name}</p>
                      <p className="text-xs text-slate-500">
                        Since {formatDate(checkout.checkedOutAt)}
                        {checkout.asset.serialNumber && ` · ${checkout.asset.serialNumber}`}
                      </p>
                    </div>
                    {checkout.kind === 'LOANER' && <Badge tone="violet">Loaner</Badge>}
                    <DueBadge dueAt={checkout.dueAt} />
                    {canWrite && (
                      <Button size="sm" onClick={() => setCheckingIn(checkout)}>
                        Check in
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader title="ChromeOS sign-ins" description="Most recent 50 events from Google Admin" />
            {person.loginEvents.length === 0 ? (
              <EmptyState icon={<Laptop />} title="No sign-in history" description="Sign-ins appear after a Google sync for people with a school email." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>When</Th>
                    <Th>Event</Th>
                    <Th>Device</Th>
                    <Th>IP address</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {person.loginEvents.map((event) => (
                    <tr key={event.id}>
                      <Td className="whitespace-nowrap">{formatDateTime(event.occurredAt)}</Td>
                      <Td>
                        <LoginEventBadge type={event.eventType} />
                      </Td>
                      <Td>
                        {event.device ? (
                          <Link to={`/devices/${event.device.id}`} className="font-mono text-xs text-brand-800 hover:underline">
                            {event.device.serialNumber}
                          </Link>
                        ) : (
                          <span className="text-xs text-slate-500">{event.deviceName ?? 'Unknown device'}</span>
                        )}
                        {event.device?.asset && <span className="ml-2 text-xs text-slate-500">{event.device.asset.assetTag}</span>}
                      </Td>
                      <Td className="font-mono text-xs text-slate-500">{event.ipAddress ?? '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <Card>
            <CardHeader title="Checkout history" />
            {past.length === 0 ? (
              <EmptyState icon={<History />} title="No past checkouts" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Asset</Th>
                    <Th>Checked out</Th>
                    <Th>Returned</Th>
                    <Th>Condition</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {past.map((checkout) => (
                    <tr key={checkout.id}>
                      <Td>
                        <Link to={`/assets/${checkout.asset.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                          {checkout.asset.assetTag}
                        </Link>
                        <p className="text-xs text-slate-500">{checkout.asset.name}</p>
                      </Td>
                      <Td className="whitespace-nowrap">{formatDate(checkout.checkedOutAt)}</Td>
                      <Td className="whitespace-nowrap">{formatDate(checkout.checkedInAt)}</Td>
                      <Td className="text-xs text-slate-600">{checkout.conditionIn ?? '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Profile" />
            <div className="p-5">
              <DescriptionList
                columns={1}
                items={[
                  { label: 'Email', value: person.email },
                  { label: 'ID number', value: person.hostId && <span className="font-mono">{person.hostId}</span> },
                  {
                    label: 'School',
                    value: person.rosterSchool && (
                      <>
                        {person.rosterSchool.name}
                        {person.rosterSchool.building && person.rosterSchool.building.name !== person.rosterSchool.name && (
                          <span className="block text-xs text-slate-500">{person.rosterSchool.building.name}</span>
                        )}
                      </>
                    ),
                  },
                  { label: 'Class of', value: person.gradYear },
                  { label: 'Grade', value: gradeLabel(person.gradeLevel) || null },
                  { label: 'Roles', value: person.roles.length ? person.roles.join(', ') : null },
                  { label: person.source === 'ONEROSTER' ? 'SIS ID' : 'Blackbaud user ID', value: person.externalId },
                  { label: 'Notes', value: person.notes && <span className="whitespace-pre-wrap">{person.notes}</span> },
                ]}
              />
            </div>
          </Card>

          <PersonFieldsCard personId={person.id} />

          {(person.classes.length > 0 || person.transfers.length > 0) && (
            <Card>
              <CardHeader title="Classes" description={person.classes.length ? `${person.classes.length} in the SIS` : 'Not in any classes right now'} />
              {person.classes.length > 0 && (
                <ul className="divide-y divide-slate-100">
                  {person.classes.map(({ id, role, class: klass }) => (
                    <li key={id} className="px-5 py-2.5 text-sm">
                      <p className="flex items-center gap-2 font-medium text-slate-900">
                        <span className="min-w-0 truncate">{klass.title}</span>
                        {role !== 'student' && <Badge tone="violet">{role === 'teacher' ? 'Teaches' : role}</Badge>}
                      </p>
                      <p className="truncate text-xs text-slate-500">
                        {[klass.classCode, klass.periods.length ? `Period ${klass.periods.join(', ')}` : null, klass.location, klass.school?.name].filter(Boolean).join(' · ')}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {person.transfers.length > 0 && (
                <div className="border-t border-slate-100 px-5 py-3">
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    <GraduationCap className="size-3.5" /> School changes
                  </p>
                  <ul className="space-y-1 text-xs text-slate-600">
                    {person.transfers.map((transfer) => (
                      <li key={transfer.id}>
                        {transfer.fromSchoolName} → <span className="font-medium text-slate-800">{transfer.toSchoolName}</span>
                        <span className="text-slate-400"> · {formatDate(transfer.detectedAt)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {person.classes[0]?.class.schoolYear && (
                <p className="border-t border-slate-100 px-5 py-2 text-xs text-slate-400">{schoolYearLabel(person.classes[0].class.schoolYear)} school year</p>
              )}
            </Card>
          )}

          <AssignedPartsCard assignee={{ type: 'PERSON', person }} />

          <Card>
            <CardHeader title="Chromebooks used" description="Devices reporting this user in Google Admin" />
            {person.devices.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-500">No devices report this user.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {person.devices.map((device) => (
                  <li key={device.id} className="px-5 py-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <Link to={`/devices/${device.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                        {device.serialNumber}
                      </Link>
                      {device.asset && (
                        <Link to={`/assets/${device.asset.id}`} className="text-xs text-slate-500 hover:text-brand-700">
                          {device.asset.assetTag}
                        </Link>
                      )}
                    </div>
                    <p className="truncate text-xs text-slate-500">
                      {device.model} · synced {formatRelative(device.lastSync)}
                      {device.annotatedUser?.toLowerCase() === person.email?.toLowerCase() && ' · assigned user'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {person.managedDevices.length > 0 && (
            <Card>
              <CardHeader title="Managed devices" description="Assigned to this person in your MDM" />
              <ul className="divide-y divide-slate-100">
                {person.managedDevices.map((device) => (
                  <li key={device.id} className="px-5 py-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <Link to={`/managed-devices/${device.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                        {device.serialNumber}
                      </Link>
                      {device.asset && (
                        <Link to={`/assets/${device.asset.id}`} className="text-xs text-slate-500 hover:text-brand-700">
                          {device.asset.assetTag}
                        </Link>
                      )}
                    </div>
                    <p className="truncate text-xs text-slate-500">
                      {[device.name, device.model].filter(Boolean).join(' · ')} · checked in {formatRelative(device.lastCheckInAt)}
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>

      <PersonFormModal open={editing} onClose={() => setEditing(false)} person={person} />
      <LendLoanerModal open={lending} onClose={() => setLending(false)} person={person} />
      <Modal open={pickingAsset} onClose={() => setPickingAsset(false)} title={`Check out to ${name}`} description="Find an available asset">
        <AssetSearch
          inline
          status={['AVAILABLE']}
          onSelect={(asset) => {
            setPickingAsset(false);
            setCheckoutAsset(asset);
          }}
        />
      </Modal>
      <CheckOutDialog
        open={!!checkoutAsset}
        onClose={() => setCheckoutAsset(null)}
        assets={checkoutAsset ? [checkoutAsset] : []}
        initialAssignee={{ type: 'PERSON', person }}
      />
      {checkingIn && <CheckInDialog open onClose={() => setCheckingIn(null)} checkout={checkingIn} />}
    </>
  );
}
